/**
 * Projects Page
 *
 * Plus-only feature for organizing links into projects.
 * Allows users to create, edit, and delete projects.
 *
 * @module pages/projects
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Layers,
  Plus,
  Loader2,
  Trash2,
  Edit2,
  X,
  Check,
  Crown,
  Link2,
  BarChart3,
  Tag,
  Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/client/hooks/use-auth';
import { apiFetch } from '@/client/lib/api';
import { OnboardingEmptyState } from '@/client/components/ui/empty-state';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Input } from '@/client/components/ui/input';
import { Skeleton } from '@/client/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';

interface ProjectType {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  linkCount: number;
  createdAt: string;
}

const PROJECT_COLORS: string[] = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#6b7280', // gray
];

const DEFAULT_PROJECT_COLOR = '#3b82f6';

export function ProjectsPage() {
  const { isPlusUser } = useAuth();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectColor, setNewProjectColor] = useState(DEFAULT_PROJECT_COLOR);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) throw new Error('Failed to fetch projects');
      return response.json();
    },
    enabled: isPlusUser,
  });

  const createMutation = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const response = await apiFetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, color }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create project');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setIsCreating(false);
      setNewProjectName('');
      setNewProjectColor(DEFAULT_PROJECT_COLOR);
      toast.success('Project created!');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name, color }: { id: string; name: string; color: string }) => {
      const response = await apiFetch(`/api/projects/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, color }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update project');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setEditingId(null);
      toast.success('Project updated!');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiFetch(`/api/projects/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete project');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleCreate = () => {
    if (!newProjectName.trim()) return;
    createMutation.mutate({ name: newProjectName.trim(), color: newProjectColor });
  };

  const handleUpdate = (id: string) => {
    if (!editName.trim()) return;
    updateMutation.mutate({ id, name: editName.trim(), color: editColor });
  };

  const handleDelete = (id: string, name: string) => {
    if (
      window.confirm(
        `Delete project "${name}"? Links in this project will be moved to "No project".`
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  const startEditing = (project: ProjectType) => {
    setEditingId(project.id);
    setEditName(project.name);
    setEditColor(project.color);
  };

  const projects: ProjectType[] = data?.data || [];

  // Non-Plus users see upgrade prompt
  if (!isPlusUser) {
    return (
      <div className="space-y-6 stagger-children">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground mt-1">Organize your links into projects</p>
        </div>

        <Card className="rounded-2xl bg-gradient-to-br from-primary/10 via-card to-accent/10 border-0 py-0">
          <CardContent className="p-12 text-center">
            <div className="size-20 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-6">
              <Layers className="size-10 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Organize with Projects</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Create projects to organize your links by campaign, client, or category. Projects make
              it easy to find and manage your links.
            </p>
            <Button asChild size="lg" className="rounded-xl glow">
              <Link to="/settings/billing">
                <Crown className="size-5" />
                Upgrade to Plus
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 stagger-children">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground mt-1">Organize your links into projects</p>
        </div>
        {!isCreating && (
          <Button onClick={() => setIsCreating(true)} size="lg" className="rounded-xl glow">
            <Plus className="size-4" />
            New Project
          </Button>
        )}
      </div>

      {/* Create Form */}
      {isCreating && (
        <Card className="rounded-xl animate-in fade-in-0 slide-in-from-top-2 py-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                {PROJECT_COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setNewProjectColor(color)}
                    className={`size-6 rounded-full transition-all cursor-pointer ${
                      newProjectColor === color ? 'ring-2 ring-offset-2 ring-primary' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <Input
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="Project name"
                className="flex-1 rounded-lg"
                autoFocus
              />
              <Button
                onClick={handleCreate}
                disabled={!newProjectName.trim() || createMutation.isPending}
                size="icon"
                className="rounded-lg"
              >
                {createMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Check className="size-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setIsCreating(false);
                  setNewProjectName('');
                }}
                className="rounded-lg"
              >
                <X className="size-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Projects List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : error ? (
        <Card className="rounded-xl border-destructive/50 bg-destructive/10 py-0">
          <CardContent className="p-6 text-center">
            <p className="text-destructive">Failed to load projects</p>
          </CardContent>
        </Card>
      ) : projects.length === 0 ? (
        <Card className="rounded-2xl py-0">
          <OnboardingEmptyState
            icon={Layers}
            title="Organize your links with projects"
            steps={[
              { icon: Layers, text: 'Create projects for different campaigns or clients' },
              { icon: Tag, text: 'Group related links together for easy access' },
              { icon: Share2, text: 'Share entire projects with your team' },
            ]}
            action={{
              label: 'Create your first project',
              onClick: () => setIsCreating(true),
              icon: Plus,
            }}
          />
        </Card>
      ) : (
        <div className="grid gap-3">
          {projects.map((project) => (
            <Card
              key={project.id}
              className="rounded-xl hover:border-primary/30 transition-colors group py-0"
            >
              <CardContent className="p-4">
                {editingId === project.id ? (
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      {PROJECT_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => setEditColor(color)}
                          className={`size-5 rounded-full transition-all cursor-pointer ${
                            editColor === color ? 'ring-2 ring-offset-1 ring-primary' : ''
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <Input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleUpdate(project.id)}
                      className="flex-1 rounded-lg h-9 text-sm"
                      autoFocus
                    />
                    <Button
                      onClick={() => handleUpdate(project.id)}
                      disabled={!editName.trim() || updateMutation.isPending}
                      size="icon-sm"
                      className="rounded-lg"
                    >
                      {updateMutation.isPending ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Check className="size-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => setEditingId(null)}
                      className="rounded-lg"
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <Link
                      to={`/projects/${project.id}`}
                      className="size-10 rounded-xl flex items-center justify-center shrink-0 hover:scale-105 transition-transform"
                      style={{ backgroundColor: `${project.color}20` }}
                    >
                      <Layers className="size-5" style={{ color: project.color }} />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/projects/${project.id}`}
                        className="font-medium hover:text-primary transition-colors"
                      >
                        {project.name}
                      </Link>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Link2 className="size-3" />
                        {project.linkCount} {project.linkCount === 1 ? 'link' : 'links'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon-sm" asChild className="rounded-lg">
                            <Link to={`/projects/${project.id}`}>
                              <BarChart3 className="size-4" />
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>View project analytics</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => startEditing(project)}
                            className="rounded-lg"
                          >
                            <Edit2 className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit project</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleDelete(project.id, project.name)}
                            disabled={deleteMutation.isPending}
                            className="rounded-lg hover:bg-destructive/10 hover:text-destructive"
                          >
                            {deleteMutation.isPending ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <Trash2 className="size-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete project</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
